import AssessmentIcon from '@mui/icons-material/Assessment';
import { Alert, Divider, Paper, Stack } from '@mui/material';
import { useTranslation } from '../../../../services/translation/hooks/useTranslation';
import { ValidationCounts } from '../../utils/validation/createValidationSummary';
import { CollectionSectionHeader } from './CollectionSectionHeader';
import { CollectionValidationItem } from './CollectionValidationItem';
type CollectionValidationSummaryProps = {
  collectionValidationCounts?: ValidationCounts;
  uploadSummaryValidationCounts?: ValidationCounts;
  uploadDetailValidationCounts?: ValidationCounts;
  scansValidationCounts?: ValidationCounts;
  alleysValidationCounts?: ValidationCounts;
  uploadsValidationCounts?: ValidationCounts;
  totalErrors: number;
  totalWarnings: number;
};

export const CollectionValidationSummary = ({
  collectionValidationCounts,
  uploadSummaryValidationCounts,
  uploadDetailValidationCounts,
  scansValidationCounts,
  alleysValidationCounts,
  uploadsValidationCounts,
  totalErrors,
  totalWarnings,
}: CollectionValidationSummaryProps) => {
  const { t } = useTranslation();

  // Skip if there are no validation issues to show
  if (
    totalErrors === 0 &&
    totalWarnings === 0 &&
    !collectionValidationCounts &&
    !uploadSummaryValidationCounts &&
    !uploadDetailValidationCounts &&
    !scansValidationCounts &&
    !alleysValidationCounts &&
    !uploadsValidationCounts
  ) {
    return null;
  }

  return (
    <Paper variant='outlined' sx={{ p: 1.5, borderRadius: 1 }}>
      <CollectionSectionHeader
        icon={
          <AssessmentIcon
            sx={(theme) => ({ color: theme.vars.palette.text.secondary })}
          />
        }
        title={t('Validation Results')}
        fontWeight={600}
        sx={{ mb: 1.5 }}
      />

      <Divider sx={{ mb: 1.5 }} />

      <Stack spacing={1.5}>
        <CollectionValidationItem
          label={t('Collection')}
          counts={collectionValidationCounts}
        />
        <CollectionValidationItem
          label={t('Upload Summary')}
          counts={uploadSummaryValidationCounts}
        />
        <CollectionValidationItem
          label={t('Upload Detail')}
          counts={uploadDetailValidationCounts}
        />
        <CollectionValidationItem
          label={t('Scans')}
          counts={scansValidationCounts}
        />
        <CollectionValidationItem
          label={t('Alleys')}
          counts={alleysValidationCounts}
        />
        <CollectionValidationItem
          label={t('Uploads')}
          counts={uploadsValidationCounts}
        />
      </Stack>

      {/* Only count validation errors from validation counts, not external errors */}
      {(collectionValidationCounts?.errors || 0) +
        (uploadSummaryValidationCounts?.errors || 0) +
        (uploadDetailValidationCounts?.errors || 0) +
        (scansValidationCounts?.errors || 0) +
        (alleysValidationCounts?.errors || 0) +
        (uploadsValidationCounts?.errors || 0) >
        0 && (
        <Alert severity='error' sx={{ mt: 1.5 }}>
          {t(
            'Validation errors detected. Please review the issues in the individual sections below.'
          )}
        </Alert>
      )}

      {/* Only show warning message if no validation errors but has validation warnings */}
      {(collectionValidationCounts?.errors || 0) +
        (uploadSummaryValidationCounts?.errors || 0) +
        (uploadDetailValidationCounts?.errors || 0) +
        (scansValidationCounts?.errors || 0) +
        (alleysValidationCounts?.errors || 0) +
        (uploadsValidationCounts?.errors || 0) ===
        0 &&
        (collectionValidationCounts?.warnings || 0) +
          (uploadSummaryValidationCounts?.warnings || 0) +
          (uploadDetailValidationCounts?.warnings || 0) +
          (scansValidationCounts?.warnings || 0) +
          (alleysValidationCounts?.warnings || 0) +
          (uploadsValidationCounts?.warnings || 0) >
          0 && (
          <Alert severity='warning' sx={{ mt: 1.5 }}>
            {t(
              'Validation warnings detected. The collection may still be processed, but please review the issues in the individual sections below.'
            )}
          </Alert>
        )}
    </Paper>
  );
};
